// Shared plumbing for fitting stacked charts inside the window.
//
// A chart column is sized from measurements of the live DOM rather than from
// assumptions about the navbar or header heights. Applying a size can change
// those measurements again - a narrower column rewraps the header bar onto more
// lines - so a fit is re-checked until it settles.

// Guards against a chart collapsing to nothing on a tiny window.
export const MIN_IMAGE_HEIGHT = 40;
// Used only until the header bar has been laid out and can be measured.
export const FALLBACK_HEADER_HEIGHT = 46;
// Every pass only ever shrinks the image, so a fit settles well inside this cap.
const MAX_REFITS = 8;

export function measureHeight(ref, fallback = 0) {
  return ref.current ? ref.current.getBoundingClientRect().height : fallback;
}

// Distance from the top of the window down to the element, i.e. the navbar plus
// whatever else the page puts above it.
export function measureTop(ref) {
  if (ref.current) {
    return ref.current.getBoundingClientRect().top + window.scrollY;
  }

  const navbar = document.querySelector(".navbar");
  return navbar ? navbar.getBoundingClientRect().height : 0;
}

export default class FitController {
  constructor({ getSignature, applyFit }) {
    this.getSignature = getSignature;
    this.applyFit = applyFit;
    this.refitCount = 0;
    this.signature = null;
    this.cancelled = false;
  }

  // Called by the owner right after it applies a fit, recording what the fit was
  // based on.
  markFitted() {
    this.signature = this.getSignature();
  }

  // A fresh fit (a resize, a new image) starts its own convergence budget.
  reset() {
    this.refitCount = 0;
  }

  // Called by the owner once the DOM reflects the last fit. Measuring is
  // synchronous on purpose: a hidden tab never runs an animation frame, and its
  // layout still has to be correct by the time it is shown.
  check() {
    if (this.cancelled || this.refitCount >= MAX_REFITS) {
      return;
    }

    if (this.getSignature() !== this.signature) {
      this.refitCount++;
      this.applyFit();
    }
  }

  cancel() {
    this.cancelled = true;
  }
}
